// @ts-check
// Ações da lixeira (doc 10, seções 9.2 e 9.5; contrato 08 da lixeira): na seleção da tabela de excluídos e na
// página do registro excluído, Restaurar e Excluir definitivamente, cada uma com confirmação e conforme o papel.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { restoreManyRecords } from '../../data/parity.js';
import { destroyManyRecords } from '../../data/trash.js';
import { canDestroyObjectRecords, canSoftDeleteObjectRecords } from '../../lib/permissions.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';
import { usePublishedSelectionTarget } from './record-selection-actions.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {'restore' | 'destroy'} TrashAction
 */

/**
 * Mensagem do resultado do lote: sucesso, ou aviso com a primeira recusa (ex.: RESTRICT de matrículas).
 * @param {TrashAction} action @param {import('../../data/parity.js').BatchResult} result
 */
function reportTrashResult(action, result) {
  const count = result.records.length;
  if (result.errors.length === 0) {
    showSuccessSnackbar(t(action === 'restore' ? 'trash.restored' : 'trash.destroyed', { count }));
    return;
  }
  const [firstError] = result.errors;
  // a recusa mais comum é RESTRICT (inscrição ou matrícula apontando para a pessoa): explica em português
  const detailedMessage = firstError.code === 'RELATION_RESTRICT_VIOLATION' ? t('trash.restrictViolation') : firstError.message;
  enqueueSnackbar({ message: t('trash.partial', { count, failed: result.errors.length }), detailedMessage, variant: 'warning' });
}

/**
 * Executa restaurar ou destruir e devolve os ids que deram certo.
 * @param {ObjectMetadata} objectMetadata @param {TrashAction} action @param {string[]} ids
 */
export async function runTrashAction(objectMetadata, action, ids) {
  const result = action === 'restore' ? await restoreManyRecords(objectMetadata, ids) : await destroyManyRecords(objectMetadata, ids);
  reportTrashResult(action, result);
  return result.records.map((record) => /** @type {string} */ (record.id));
}

/**
 * Confirmação de restaurar ou excluir definitivamente (o Twenty pede confirmação para as duas).
 * @param {{ action: TrashAction, count: number, isLoading?: boolean, onClose: () => void, onConfirm: () => void }} props
 */
export function TrashConfirmModal({ action, count, isLoading = false, onClose, onConfirm }) {
  const isDestroy = action === 'destroy';
  return html`<${Modal}
    title=${t(isDestroy ? 'trash.confirmDestroyTitle' : 'trash.confirmRestoreTitle', { count })}
    subtitle=${isDestroy ? t('trash.confirmDestroySubtitle', { count }) : t('trash.confirmRestoreSubtitle')}
    confirmLabel=${t(isDestroy ? 'trash.destroy' : 'trash.restore')}
    confirmAccent=${isDestroy ? 'danger' : 'blue'}
    isLoading=${isLoading}
    onClose=${onClose}
    onConfirm=${onConfirm}
  />`;
}

/**
 * Barra da seleção na tabela de excluídos: só Restaurar e Excluir definitivamente.
 * @param {{ objectMetadata: ObjectMetadata, selectedCount: number, resolveSelectedIds: () => Promise<string[]>,
 *   onClear: () => void, onRemoved: (ids: string[]) => void, selectAllPrompt?: any, view?: import('../../data/metadata-store.js').View | null }} props
 *   view: com ela, a seleção da lixeira também vale no menu de comandos e no clique direito
 */
export function TrashSelectionActions({ objectMetadata, selectedCount, resolveSelectedIds, onClear, onRemoved, selectAllPrompt, view = null }) {
  const [pendingAction, setPendingAction] = useState(/** @type {TrashAction | null} */ (null));
  usePublishedSelectionTarget({
    objectMetadata, view: /** @type {any} */ (view ?? { id: '', viewFields: [], mainGroupByFieldMetadataId: null }), count: view ? selectedCount : 0, isTrashMode: true,
    isAllMatchingSelected: false, resolveSelectedIds, onChanged: () => {}, onDeleted: onRemoved, onClear,
  });
  const [isBusy, setIsBusy] = useState(false);
  const canRestore = canSoftDeleteObjectRecords(objectMetadata);
  const canDestroy = canDestroyObjectRecords(objectMetadata);

  const confirm = async () => {
    if (!pendingAction) return;
    setIsBusy(true);
    try {
      const doneIds = await runTrashAction(objectMetadata, pendingAction, await resolveSelectedIds());
      onRemoved(doneIds);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
      setPendingAction(null);
    }
  };

  return html`<div class="table-selection-bar" role="toolbar" aria-label=${t('parity.bulk.toolbar')} data-testid="trash-selection-bar">
    <span>${t('table.selectedCount', { count: selectedCount })}</span>
    ${selectAllPrompt ?? null}
    ${canRestore
      ? html`<button type="button" class="light-button selection-action" data-testid="trash-restore" disabled=${isBusy} onClick=${() => setPendingAction('restore')}>
          <${Icon} name="IconRestore" size=${14} /><span>${t('trash.restore')}</span>
        </button>`
      : null}
    ${canDestroy
      ? html`<button type="button" class="light-button selection-action selection-action-danger" data-testid="trash-destroy" disabled=${isBusy} onClick=${() => setPendingAction('destroy')}>
          <${Icon} name="IconTrashX" size=${14} /><span>${t('trash.destroy')}</span>
        </button>`
      : null}
    <button type="button" class="light-button" onClick=${onClear}>${t('table.clearSelection')}</button>
    ${pendingAction
      ? html`<${TrashConfirmModal} action=${pendingAction} count=${selectedCount} isLoading=${isBusy} onClose=${() => setPendingAction(null)} onConfirm=${confirm} />`
      : null}
  </div>`;
}
